import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchDailyUsage } from '../api/questions';
import { useIsPremium } from '../stores/premiumStore';
import { useAuthStore } from '../stores/authStore';
import type { DailyUsage } from '../types/questions';

export default function DailyLimitBanner() {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isPremium = useIsPremium();

  const { data } = useQuery<DailyUsage>({
    queryKey: ['daily-usage'],
    queryFn: fetchDailyUsage,
    enabled: !!isAuthenticated && !isPremium,
    refetchInterval: 60000,
  });

  if (!isAuthenticated || isPremium || !data?.is_limited) return null;

  const pct = Math.min((data.used / (data.limit ?? 20)) * 100, 100);
  const isNearLimit = data.remaining !== null && data.remaining <= 5;
  const isAtLimit = data.remaining === 0;

  return (
    <div
      className={`rounded-2xl border border-slate-200/70 bg-white p-4 mb-4 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_1px_1px_rgba(15,23,42,0.04)] dark:border-white/10 dark:bg-card-dark ${
        isAtLimit
          ? 'ring-1 ring-red-500/30'
          : isNearLimit
            ? 'ring-1 ring-amber-500/30'
            : ''
      }`}
      role="status"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-white">
            {isAtLimit
              ? t('components.dailyLimit.limitReached')
              : t('components.dailyLimit.usage', { used: data.used, limit: data.limit })}
          </p>
          <div
            className="mt-1.5 h-2 w-full max-w-[200px] rounded-full bg-slate-200 dark:bg-white/10"
            role="progressbar"
            aria-valuenow={data.used}
            aria-valuemax={data.limit ?? 20}
            aria-label={t('components.dailyLimit.progressLabel')}
          >
            <div
              className={`h-full rounded-full transition-all ${
                isAtLimit
                  ? 'bg-gradient-to-r from-red-500 to-red-400'
                  : isNearLimit
                    ? 'bg-gradient-to-r from-amber-500 to-amber-400'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <Link
          to="/pricing"
          className="flex-shrink-0 rounded-lg bg-[linear-gradient(135deg,#F59E0B,#D97706)] px-3 py-1.5 text-xs font-extrabold text-[#1A1205] shadow-[0_6px_16px_-6px_rgba(245,158,11,0.5)] transition-transform hover:-translate-y-px"
        >
          {t('components.dailyLimit.upgrade')}
        </Link>
      </div>
    </div>
  );
}
